"use strict";
/**
 * Buying limits — ONE evaluation used by the till (compliance-service POS route),
 * online checkout and the customer account, so every channel counts the same way.
 *
 * Limits live on the compliance PRESET (maxPerTransaction / dailyPurchaseLimit /
 * weeklyPurchaseLimit). A product's category maps to a preset through the tenant's
 * category rules (location override first, then '__all__'). Past purchases come
 * from the customer's orders (POS and online share the orders table); each order
 * line carries `categoryId`.
 *
 * Windows are ROLLING: "daily" = the last 24 hours, "weekly" = the last 7 days.
 * Spec: docs/superpowers/specs/2026-10-06-customer-accounts-design.md (Phase 2).
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.WEEK_MS = exports.DAY_MS = void 0;
exports.presetHasLimit = presetHasLimit;
exports.buildCategoryPresetMap = buildCategoryPresetMap;
exports.evaluateBuyingLimits = evaluateBuyingLimits;
exports.DAY_MS = 24 * 60 * 60 * 1000;
exports.WEEK_MS = 7 * exports.DAY_MS;
const VOID_STATUSES = new Set(['cancelled', 'canceled', 'refunded', 'rejected', 'voided', 'void']);
const positive = (v) => typeof v === 'number' && Number.isFinite(v) && v > 0 ? Math.floor(v) : null;
/** True when the preset sets at least one buying limit. */
function presetHasLimit(p) {
    return !!p && p.isActive !== false
        && (positive(p.maxPerTransaction) !== null || positive(p.dailyPurchaseLimit) !== null || positive(p.weeklyPurchaseLimit) !== null);
}
/** categoryId → presetId, location override winning over the '__all__' default. */
function buildCategoryPresetMap(rules, locationId) {
    const map = new Map();
    const loc = (r) => r.locationId || (typeof r.locationCategoryId === 'string' ? r.locationCategoryId.split('#')[0] : '__all__');
    for (const r of rules)
        if (r.categoryId && r.presetId && loc(r) === '__all__')
            map.set(r.categoryId, r.presetId);
    if (locationId)
        for (const r of rules)
            if (r.categoryId && r.presetId && loc(r) === locationId)
                map.set(r.categoryId, r.presetId);
    return map;
}
const lineToPreset = (l, cat) => (l.presetId || l.compliancePresetId || (l.categoryId ? cat.get(l.categoryId) : undefined)) ?? null;
/**
 * Evaluate a customer's limits. `requested` is the cart being sold now (may be
 * empty, e.g. the account page). Only presets with at least one limit appear.
 */
function evaluateBuyingLimits(input) {
    const now = input.now ?? Date.now();
    const limited = new Map(input.presets.filter(presetHasLimit).map((p) => [p.presetId, p]));
    const usedDay = new Map();
    const usedWeek = new Map();
    const oldestDay = new Map();
    const oldestWeek = new Map();
    for (const o of input.orders) {
        if (!o || typeof o.createdAt !== 'number' || VOID_STATUSES.has(String(o.status || '').toLowerCase()))
            continue;
        const age = now - o.createdAt;
        if (age < 0 || age >= exports.WEEK_MS)
            continue;
        for (const l of o.items || []) {
            const pid = lineToPreset(l, input.categoryPresets);
            if (!pid || !limited.has(pid))
                continue;
            const q = Math.max(0, Number(l.quantity) || 0);
            if (!q)
                continue;
            usedWeek.set(pid, (usedWeek.get(pid) || 0) + q);
            oldestWeek.set(pid, Math.min(oldestWeek.get(pid) ?? Infinity, o.createdAt));
            if (age < exports.DAY_MS) {
                usedDay.set(pid, (usedDay.get(pid) || 0) + q);
                oldestDay.set(pid, Math.min(oldestDay.get(pid) ?? Infinity, o.createdAt));
            }
        }
    }
    const requested = new Map();
    for (const l of input.requested || []) {
        const pid = lineToPreset(l, input.categoryPresets);
        if (!pid || !limited.has(pid))
            continue;
        requested.set(pid, (requested.get(pid) || 0) + Math.max(0, Number(l.quantity) || 0));
    }
    const presets = [];
    const violations = [];
    for (const [pid, p] of limited) {
        const perSale = positive(p.maxPerTransaction);
        const daily = positive(p.dailyPurchaseLimit);
        const weekly = positive(p.weeklyPurchaseLimit);
        const d = usedDay.get(pid) || 0;
        const w = usedWeek.get(pid) || 0;
        const r = requested.get(pid) || 0;
        const name = p.name || pid;
        presets.push({
            presetId: pid, name, perSale, daily, weekly, usedDay: d, usedWeek: w, requested: r,
            remainingDay: daily === null ? null : Math.max(0, daily - d),
            remainingWeek: weekly === null ? null : Math.max(0, weekly - w),
            dayFreesAt: oldestDay.has(pid) ? oldestDay.get(pid) + exports.DAY_MS : null,
            weekFreesAt: oldestWeek.has(pid) ? oldestWeek.get(pid) + exports.WEEK_MS : null,
        });
        if (r > 0) {
            if (perSale !== null && r > perSale)
                violations.push({ presetId: pid, name, period: 'sale', limit: perSale, alreadyPurchased: 0, requested: r });
            if (daily !== null && d + r > daily)
                violations.push({ presetId: pid, name, period: 'day', limit: daily, alreadyPurchased: d, requested: r });
            if (weekly !== null && w + r > weekly)
                violations.push({ presetId: pid, name, period: 'week', limit: weekly, alreadyPurchased: w, requested: r });
        }
    }
    presets.sort((a, b) => a.name.localeCompare(b.name));
    return { ok: violations.length === 0, presets, violations };
}
//# sourceMappingURL=buyingLimits.js.map